/**
 * Acompanhar (E144.F4, GUS-105 + GUS-135, ADR-0098 §2.4): o recibo dos casos do envio — uma linha
 * por CCB, com o código do caso, o exame e "Abrir caso" — e o rodapé.
 *
 * Chega com o recibo que a conclusão devolveu; recarregada, lê a conclusão gravada (ler não
 * conclui) e a entrega à jornada (`onLida`). Sem conclusão, aponta o Conferir.
 *
 * E165.F1.T07 (HTML 6.8, ADR-0114 §4): o título é "Envio recebido" e o card, "Acompanhar". Cada
 * linha diz, depois do exame, a nova análise com o caso de origem, o vínculo ao caso existente, a
 * CCB já recebida ou o caso do escritório — nunca "caso aberto" quando o envio só vinculou. O rodapé
 * tem só "Voltar aos casos" e "Enviar documentos"; "Editar caso" saiu (a identificação se edita no
 * card do caso). O titular não aparece: o envio não o confere.
 *
 * E177.F5 (GUS-253, HTML 6.8 por CCB `?tela=recibo-ccb`): a CCB declarada é "CCB 4201 · código", e a
 * linha conta os documentos que o caso recebeu e os compartilhados com outra CCB do envio (o recibo
 * gravado antes não traz as contagens e não as mostra).
 *
 * E177.F5.C01 (GUS-253 item 4): a linha da CCB mantida diante do identificador divergente diz o que
 * o pacote indica; o caso segue com o número informado.
 *
 * E193.F1 (GUS-259): a linha do caso digital aguardando arquivos diz o que falta.
 *
 * E193.F1.C10 (F-E193.F1-06): enquanto a conferência dos arquivos não termina, a linha diz que ela
 * está em andamento, e a tela relê até o desfecho (`usePendentesDoEnvio`).
 */
import { useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import { ArquivosPendentes } from '@/components/ArquivosPendentes/ArquivosPendentes';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  lerConclusao,
  type CasoDoEnvioData,
  type ConclusaoDaAberturaData,
} from '@/lib/conclusao_da_abertura';

import { BOTAO_DO_RODAPE, CartaoDaAbertura, RODAPE_DA_JORNADA } from './PecasDaAbertura';
import { textoDasContagens } from './conferenciaPorCcb';
import { complementoDaLinha, rotuloDoContrato } from './rotulosDoEnvio';
import { kitDoCaso, usePendentesDoEnvio, type KitDoCaso } from './usePendentesDoEnvio';

const t = ptBR.abertura.acompanhar;

export interface AcompanharEnvioProps {
  client: AxiosInstance;
  caseId: string;
  /** O recibo que a conclusão acabou de devolver; sem ele, a tela lê o gravado. */
  conclusao: ConclusaoDaAberturaData | null;
  /** E163.F3: a conclusão lida do servidor sobe à jornada, que põe o título. */
  onLida?: (conclusao: ConclusaoDaAberturaData) => void;
  onAbrirCaso: (caseId: string) => void;
  onVoltarAosCasos: () => void;
  /** "Enviar documentos": um envio novo. */
  onNovoCaso: () => void;
  onConferir: () => void;
}

type Estado = ConclusaoDaAberturaData | 'carregando' | 'nao_concluido' | 'erro';

function LinhaDoCaso({
  caso,
  kit,
  onAbrir,
}: {
  caso: CasoDoEnvioData;
  kit: KitDoCaso;
  onAbrir: () => void;
}): JSX.Element {
  const { pendentes, conferencia } = kit;
  const complemento = complementoDaLinha(caso);
  const documentos = caso.documentos ?? 0;
  const contagens =
    documentos > 0
      ? ` · ${textoDasContagens({ documentos, compartilhados: caso.compartilhados ?? 0 })}`
      : null;
  return (
    <li
      className="flex flex-wrap items-center justify-between gap-[15px] border-b border-border py-3 last:border-b-0"
      data-testid={`acompanhar-caso-${caso.case_id}`}
    >
      <span className="min-w-0">
        <b className="block text-[14px] font-semibold text-fg">
          {rotuloDoContrato(caso)} · <span className="font-mono">{caso.codigo}</span>
        </b>
        <small className="mt-1 block text-[13px] text-fg-muted">
          {ptBR.abertura.servico.eletronica.nome}
          {contagens}
          {complemento !== null ? ` · ${complemento}` : null}
          {caso.divergencia_mantida != null ? (
            <span data-testid={`acompanhar-divergencia-${caso.case_id}`}>
              {` · ${ptBR.abertura.conferirPorCcb.divergenteNoRecibo
                .replace(
                  '{fonte}',
                  ptBR.abertura.conferirEnvio.fonte[caso.divergencia_mantida.fonte],
                )
                .replace('{lido}', caso.divergencia_mantida.valor)}`}
            </span>
          ) : null}
        </small>
      </span>
      <Button type="button" variant="secondary" className={BOTAO_DO_RODAPE} onClick={onAbrir}>
        {t.abrirCaso}
      </Button>
      {conferencia === 'em_andamento' || conferencia === 'falhou' ? (
        <p
          role="status"
          className="basis-full text-[13px] text-fg-muted"
          data-testid={`acompanhar-conferindo-${caso.case_id}`}
        >
          {ptBR.arquivosPendentes.conferindo}
        </p>
      ) : null}
      {conferencia === 'sem_desfecho' ? (
        <p
          className="basis-full text-[13px] text-fg-muted"
          data-testid={`acompanhar-sem-desfecho-${caso.case_id}`}
        >
          {ptBR.arquivosPendentes.conferenciaSemDesfecho}
        </p>
      ) : null}
      {pendentes.length ? (
        <div className="basis-full">
          <ArquivosPendentes
            compacto
            pendentes={pendentes}
            texto={ptBR.arquivosPendentes.textoDoEnvio}
            testId={`acompanhar-pendentes-${caso.case_id}`}
          />
        </div>
      ) : null}
    </li>
  );
}

export function AcompanharEnvio(props: AcompanharEnvioProps): JSX.Element {
  const { client, caseId, onLida } = props;
  const [lido, setLido] = useState<Estado>('carregando');
  const estado: Estado = props.conclusao ?? lido;
  // E193.F1 (GUS-259): a lista do que falta chega depois da conclusão; relida até o desfecho.
  const relidos = usePendentesDoEnvio(client, caseId, typeof estado === 'object' ? estado : null);

  useEffect(() => {
    if (props.conclusao !== null) return;
    let ativo = true;
    lerConclusao(client, caseId).then(
      (lida) => {
        if (!ativo) return;
        setLido(lida ?? 'nao_concluido');
        if (lida !== null) onLida?.(lida);
      },
      () => ativo && setLido('erro'),
    );
    return () => {
      ativo = false;
    };
  }, [client, caseId, props.conclusao, onLida]);

  if (estado === 'carregando') return <p className="text-body-sm text-fg-muted">{t.carregando}</p>;
  if (estado === 'erro' || estado === 'nao_concluido') {
    return (
      <div className="flex flex-col gap-sc-4" data-testid="acompanhar-sem-conclusao">
        <p role="alert" className="text-body-sm text-danger">
          {estado === 'erro' ? t.erro : t.naoConcluido}
        </p>
        {estado === 'nao_concluido' ? (
          <Button type="button" className="self-start" onClick={props.onConferir}>
            {t.irConferir}
          </Button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-5" data-testid="abertura-passo-acompanhar">
      <CartaoDaAbertura titulo={t.cardTitulo} testId="acompanhar-dados">
        <ul>
          {estado.casos.map((caso) => (
            <LinhaDoCaso
              key={caso.case_id}
              caso={caso}
              kit={relidos?.[caso.case_id] ?? kitDoCaso(caso)}
              onAbrir={() => props.onAbrirCaso(caso.case_id)}
            />
          ))}
        </ul>
      </CartaoDaAbertura>
      <div className={RODAPE_DA_JORNADA}>
        <Button
          type="button"
          variant="secondary"
          className={BOTAO_DO_RODAPE}
          onClick={props.onVoltarAosCasos}
        >
          {t.voltarAosCasos}
        </Button>
        <Button
          type="button"
          className={BOTAO_DO_RODAPE}
          data-testid="acompanhar-enviar-documentos"
          onClick={props.onNovoCaso}
        >
          {t.enviarDocumentos}
        </Button>
      </div>
    </div>
  );
}
